import React, { useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Tooltip } from 'antd';
import Icon from '@ant-design/icons';
import { HeaderSection, HeaderTitle, HeaderControls, DrilldownIconButton } from '../styled';
import OpenOutIcon from '@icons/common/open-out.svg';
import CountPercentageTabs from '@src/components/common/CountPercentageTabs';
import MetricSelectorPopup from '@components/common/MetricSelectorPopup';
import {
  selectedMetricsAtom,
  metricUnitAtom,
  appliedFilterAtom,
  appliedIntervalAtom,
  currentKpiIdAtom,
} from '../../atoms';
import { supportedMetricsSelector } from '@src/store/common/selectors';
import { getInvertedPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { DEFAULT_DIMENSIONS } from '@src/store/trends/base/reducer';
import { createTempDashboard } from '@src/store/tempDashboard/actions';
import { DASHBOARD_TYPE } from '@src/utils/constants';
import { getLimitationByUsecaseKey } from '@hooks/useCurrentLimitation';
import useGlobalExcludeMetricCategories from '@src/hooks/metrics/useGlobalExcludeMetricCategories';
import useSupportedMetrics from '@hooks/metrics/useSupportedMetrics';

interface HeaderProps {
  metricsToFetch: string[];
}

const Header: React.FC<HeaderProps> = ({ metricsToFetch }) => {
  const [selectedMetrics, setSelectedMetrics] = useAtom(selectedMetricsAtom);
  const [currentKpiId, setCurrentKpiId] = useAtom(currentKpiIdAtom);
  const [unit, setUnit] = useAtom(metricUnitAtom);
  const appliedFilter = useAtomValue(appliedFilterAtom);
  const interval = useAtomValue(appliedIntervalAtom);
  const dispatch = useDispatch();

  // Get limitation and metrics
  const limitation = getLimitationByUsecaseKey('overview');
  const excludeMetricCategories = useGlobalExcludeMetricCategories([]);
  const supportedMetrics: Record<string, any> = useSelector(supportedMetricsSelector);

  const metrics = useSupportedMetrics({
    excludeCategories: excludeMetricCategories,
    limitation,
    allMetrics: supportedMetrics,
  });

  const handleUnitToggle = useCallback(
    (value: '#' | '%') => {
      setUnit(value);
    },
    [setUnit],
  );

  const onApply = useCallback(
    (newMetrics: string[]) => {
      setSelectedMetrics(newMetrics);
    },
    [setSelectedMetrics],
  );

  // Handle panel-level drilldown - opens all metrics in app-experience dashboard
  const handleDrilldown = useCallback(() => {
    if (!metricsToFetch || metricsToFetch.length === 0) return;

    // Get all inverted metrics (metricsToFetch already handles percentage conversion)
    const allMetrics = metricsToFetch.map((metricKey) => {
      // Get inverted metric (for percentage metrics, this converts them back)
      return getInvertedPercentageMetricByKey({
        metricKey,
        defaultValue: metricKey,
      });
    });

    // Remove duplicates
    const uniqueMetrics = Array.from(new Set(allMetrics));

    if (uniqueMetrics.length === 0) return;

    const dashboardConfig = {
      metricsChartContainer: {
        metric: uniqueMetrics[0],
        metrics: uniqueMetrics,
      },
      common: {
        appliedFilters: [
          {
            name: appliedFilter.name,
            rule: appliedFilter.rule || [],
            fid: appliedFilter.fid,
          },
        ],
        interval,
      },
      'module:trends': {
        base: {
          dimensions: [...DEFAULT_DIMENSIONS.APP],
          dimensionMetrics: [uniqueMetrics],
        },
      },
      metricPanel: {
        layout: '1s',
        widgetPanelVisibility: false,
        percentile: 'p90',
      },
      toggles: {
        sections: {
          summary: false,
        },
      },
    };

    const title = 'Overview Metrics';

    dispatch(
      createTempDashboard(DASHBOARD_TYPE.APP_EXPERIENCE_DRILLDOWN, dashboardConfig, title, {
        newBrowserTab: true,
      }),
    );
  }, [metricsToFetch, interval, appliedFilter, dispatch]);

  return (
    <HeaderSection>
      <HeaderTitle>
        <div>Metrics</div>
        {selectedMetrics.length > 0 && (
          <Tooltip title="Open all metrics in trends" placement="top">
            <DrilldownIconButton
              size="small"
              icon={<Icon component={OpenOutIcon} />}
              type="text"
              onClick={handleDrilldown}
            />
          </Tooltip>
        )}
      </HeaderTitle>
      <HeaderControls>
        <CountPercentageTabs onToggle={handleUnitToggle} value={unit} />
        <MetricSelectorPopup
          supportedMetrics={metrics}
          selectedMetrics={selectedMetrics}
          lockedMetrics={[]}
          RenderBtn={Button}
          onApply={onApply}
          metricsCountLimit={10}
          config={limitation?.metrics?.config}
          currentKpiId={currentKpiId}
          onKpiChange={setCurrentKpiId}
        />
      </HeaderControls>
    </HeaderSection>
  );
};

export default Header;
